import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useSwipeToClose from "../../../hooks/useSwipeToClose";
import { useUser } from "../../../contexts/UserContext";

function MealPlanDetailsDrawer({ 
  isOpen, 
  onClose, 
  onTryAgain,
  isPremium = false
}) {
  // Get user preferences from context
  const { 
    numberOfPeople, 
    budget, 
    selectedCuisine, 
    dietaryPreferences,
    cookingStyle
  } = useUser();

  const { t } = useTranslation('mealplan');
  const { t: tCommon } = useTranslation('common');
  const { t: tWizard } = useTranslation('wizard');

  // Helper to get cooking style display (similar to UserWizard)
  const getCookingStyleDisplay = () => {
    if (!cookingStyle) return [{ label: tWizard('cookingStyle.display.allStandard'), has2Days: false }];
    
    const parts = [];
    const mealTypes = ['breakfast', 'lunch', 'dinner'];
    
    mealTypes.forEach(mealType => {
      const style = cookingStyle[mealType];
      const complexity = style?.cookingTime || 'standard';
      const cookFor2Days = style?.cookFor2Days || false;
      
      // Only show non-standard settings
      if (complexity !== 'standard' || cookFor2Days) {
        let label = '';
        if (complexity === 'simple') {
          label = tWizard(`cookingStyle.display.simple${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`);
        } else if (complexity === 'advanced') {
          label = tWizard(`cookingStyle.display.advanced${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`);
        } else {
          label = tWizard(`cookingStyle.${mealType}`);
        }
        
        parts.push({
          label,
          has2Days: cookFor2Days
        });
      }
    });
    
    if (parts.length === 0) return [{ label: tWizard('cookingStyle.display.allStandard'), has2Days: false }];
    return parts;
  };

  const {
    containerRef,
    dragOffset,
    isAnimating,
    shouldRender,
    isVisible,
    DragHandle,
  } = useSwipeToClose(onClose, isOpen);

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`fixed flex items-end justify-center inset-0 bg-[#0D0C07] z-[200] drawer-overlay`}
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition:
          "background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      }}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className={`flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content overflow-hidden`}
        style={{
          maxHeight: 'calc(100dvh - 44px - env(safe-area-inset-top, 0px))',
          height: 'auto',
          transform: isVisible
            ? `translateY(${Math.max(0, dragOffset)}px)`
            : "translateY(100%)",
          transition:
            dragOffset === 0
              ? "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        <DragHandle />
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z"
              fill="#56554D"
            />
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-6 px-6 pb-[108px]">
          <div className="pt-2 w-full">
            {/* Header */}
            <div className="pb-3 w-full">
              <h2 className="text-[#0d0c07] text-[24px] font-bold leading-[40px]">
                {t('myPreferences')}
              </h2>
            </div>

            {/* Info Card - Yellow (hidden for premium users) */}
            {!isPremium && (
            <div class="bg-[#ffcb00] relative rounded-[12px] border-[0px] border-[#0d0c07] mb-4">
             <div className="flex gap-3 items-start px-4 py-3">
                {/* Icon */}
                <div className="flex items-center justify-center h-7 py-1 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M10.5 3L8 9L12 22M12 22L16 9L13.5 3M12 22C12.3172 22 12.6293 21.9245 12.9116 21.7799C13.1939 21.6352 13.4378 21.4255 13.623 21.168L21.613 10.182C21.8665 9.83605 22.0021 9.41776 21.9998 8.98885C21.9975 8.55995 21.8573 8.14316 21.6 7.8L18.6 3.8C18.4137 3.55161 18.1721 3.35 17.8944 3.21115C17.6167 3.07229 17.3105 3 17 3H7C6.68891 3.00004 6.38213 3.07264 6.10403 3.21204C5.82593 3.35144 5.58418 3.55379 5.398 3.803L2.4 7.8C2.14256 8.14306 2.00222 8.55979 1.9997 8.98869C1.99718 9.41759 2.13261 9.83594 2.386 10.182L10.376 21.168C10.5612 21.4255 10.8051 21.6352 11.0874 21.7799C11.3696 21.9245 11.6828 22 12 22ZM2 9H22" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                </div>
                {/* Text */}
                <p className="text-[#0d0c07] text-[14px] leading-[28px]">
                  {tCommon('premium.aiLearnPreferences', { ns: 'common' })} &nbsp;
                  <a href="/premium" className="font-semibold border-b-[1px] text-[#4D4B42] md:hover:border-[#4D4B42] border-[#0d0c07]">{tCommon('premium.discoverMore', { ns: 'common' })}</a>
                </p>
              </div>
            </div>
            )}
            {/* Cards Container */}
            <div className="flex flex-col gap-3">
              {/* Grocery Budget Card (combined with number of people) */}
              <div
                className="
                  wizard-component wizard-cuisine-section
                  flex
                  min-h-[48px]
                  pt-2
                  pb-4
                  p-3
                  items-start
                  self-stretch
                  rounded-[var(--Border-Radius-Outer,12px)]
                  border-[0.5px]
                  border-[var(--Color-Grays-Gray-8,#56554D)]
                  bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
                  transition-all
                  shadow-sm
                "
              >
                <div className="w-7 h-7 flex items-center justify-center mr-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10 1.66675V18.3334M14.1667 4.16675H7.91667C7.14312 4.16675 6.40125 4.47404 5.85427 5.02102C5.30729 5.568 5 6.30987 5 7.08341C5 7.85696 5.30729 8.59883 5.85427 9.14581C6.40125 9.69279 7.14312 10.0001 7.91667 10.0001H12.0833C12.8569 10.0001 13.5987 10.3074 14.1457 10.8544C14.6927 11.4013 15 12.1432 15 12.9167C15 13.6903 14.6927 14.4322 14.1457 14.9791C13.5987 15.5261 12.8569 15.8334 12.0833 15.8334H5"
                      stroke="#0D0C07"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-grow">
                  <h2
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[15.2px]
                      font-semibold
                      leading-[28px]
                      mb-1
                    "
                  >
                    {tWizard('sections.groceryBudget')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <div
                      className="
                        text-[var(--Color-Grays-Gray-10,#0D0C07)]
                        text-[14px]
                        font-normal
                        leading-[24px]
                        bg-[var(--Color-Brand-Primary,#E2E1DF)]
                        px-3 py-1
                        rounded-[6px]
                        inline-block
                      "
                    >
                      {budget?.label || tCommon('premium.notSpecified')}
                    </div>
                    <div
                      className="
                        text-[var(--Color-Grays-Gray-10,#0D0C07)]
                        text-[14px]
                        font-normal
                        leading-[24px]
                        bg-[var(--Color-Brand-Primary,#E2E1DF)]
                        px-3 py-1
                        rounded-[6px]
                        inline-block
                      "
                    >
                      {numberOfPeople} {numberOfPeople === 1 ? tWizard('people.person') : tWizard('people.people')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cooking Style Card */}
              <div
                className="
                  wizard-component wizard-cuisine-section
                  flex
                  min-h-[48px]
                  pt-2
                  pb-4
                  p-3
                  items-start
                  self-stretch
                  rounded-[var(--Border-Radius-Outer,12px)]
                  border-[0.5px]
                  border-[var(--Color-Grays-Gray-8,#56554D)]
                  bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
                  transition-all
                  shadow-sm
                "
              >
                <div className="w-7 h-7 flex items-center justify-center mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="#0D0C07" strokeWidth="1.75"/>
                    <path d="M10 5V10L13.3333 11.6667" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="flex-grow">
                  <h2
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[15.2px]
                      font-semibold
                      leading-[28px]
                      mb-1
                    "
                  >
                    {tWizard('sections.cookingStyle')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {getCookingStyleDisplay().map((item, index) => (
                      <div
                        key={index}
                        className="
                          text-[var(--Color-Grays-Gray-10,#0D0C07)]
                          text-[14px]
                          font-normal
                          leading-[24px]
                          bg-[var(--Color-Brand-Primary,#E2E1DF)]
                          px-3 py-1
                          rounded-[6px]
                          inline-block
                        "
                      >
                        {item.label}
                        {item.has2Days && (
                          <>
                            <span className="text-[rgba(13,12,7,0.7)]"> • </span>
                            <span>2 {tWizard('cookingStyle.display.days')}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Cuisines Card */}
              <div
                className="
                  wizard-component wizard-cuisine-section
                  flex
                  min-h-[48px]
                  pt-2
                  pb-4
                  p-3
                  items-start
                  self-stretch
                  rounded-[var(--Border-Radius-Outer,12px)]
                  border-[0.5px]
                  border-[var(--Color-Grays-Gray-8,#56554D)]
                  bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
                  transition-all
                  shadow-sm
                "
              >
                <div className="w-7 h-7 flex items-center justify-center mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.99979 14.1666H14.9998M14.1665 17.5C14.3875 17.5 14.5994 17.4122 14.7557 17.2559C14.912 17.0996 14.9998 16.8876 14.9998 16.6666V12.2083C14.9998 11.8275 15.2631 11.505 15.6056 11.3408C16.3148 11.0025 16.8816 10.4246 17.2061 9.70904C17.5307 8.99348 17.592 8.1863 17.3793 7.42993C17.1666 6.67356 16.6936 6.01663 16.0437 5.57508C15.3938 5.13353 14.6088 4.93575 13.8273 5.01663C13.5054 4.26847 12.9714 3.63104 12.2911 3.18316C11.6109 2.73528 10.8143 2.49658 9.99979 2.49658C9.18534 2.49658 8.38874 2.73528 7.70848 3.18316C7.02823 3.63104 6.49416 4.26847 6.17229 5.01663C5.39111 4.93631 4.60666 5.13438 3.95724 5.57591C3.30782 6.01744 2.83514 6.67408 2.62253 7.43005C2.40993 8.18602 2.47105 8.99278 2.79513 9.70809C3.11921 10.4234 3.68543 11.0013 4.39396 11.34C4.73646 11.505 4.99979 11.8275 4.99979 12.2075V16.6666C4.99979 16.8876 5.08759 17.0996 5.24387 17.2559C5.40015 17.4122 5.61211 17.5 5.83313 17.5H14.1665Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="flex-grow">
                  <h2
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[15.2px]
                      font-semibold
                      leading-[28px]
                      mb-1
                    "
                  >
                    {tWizard('sections.cuisine')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {selectedCuisine && selectedCuisine.split(', ').map((cuisine, index) => (
                      <div
                        key={index}
                        className="
                          text-[var(--Color-Grays-Gray-10,#0D0C07)]
                          text-[14px]
                          font-normal
                          leading-[24px]
                          bg-[var(--Color-Brand-Primary,#E2E1DF)]
                          px-3 py-1
                          rounded-[6px]
                          inline-block
                        "
                      >
                        {cuisine}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dietary Preferences Card */}
              <div
                className="
                  wizard-component wizard-cuisine-section
                  flex
                  min-h-[48px]
                  pt-2
                  pb-4
                  p-3
                  items-start
                  self-stretch
                  rounded-[var(--Border-Radius-Outer,12px)]
                  border-[0.5px]
                  border-[var(--Color-Grays-Gray-8,#56554D)]
                  bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
                  transition-all
                  shadow-sm
                "
              >
                <div className="w-7 h-7 flex items-center justify-center mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g clipPath="url(#clip0_2268_14865)">
                      <path d="M13.3333 6.66675C16.6666 6.66675 18.3333 5.00008 18.3333 1.66675C15 1.66675 13.3333 3.33341 13.3333 6.66675ZM13.3333 6.66675C11.25 10.4167 10.75 12.6501 9.99996 18.3334C9.9039 15.117 9.10871 11.96 7.66964 9.08191C6.23057 6.20377 4.18213 3.67345 1.66663 1.66675M14.5083 2.99175C12.779 1.8793 10.6994 1.44549 8.66959 1.77372C6.63973 2.10196 4.8029 3.16908 3.51233 4.76985C2.22175 6.37062 1.56858 8.39202 1.67841 10.4453C1.78824 12.4986 2.65332 14.4388 4.10729 15.8928C5.56126 17.3467 7.50145 18.2118 9.55473 18.3216C11.608 18.4315 13.6294 17.7783 15.2302 16.4877C16.831 15.1971 17.8981 13.3603 18.2263 11.3305C18.5546 9.3006 18.1207 7.22106 17.0083 5.49175" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_2268_14865">
                        <rect width="20" height="20" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div className="flex-grow">
                  <h2
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[15.2px]
                      font-semibold
                      leading-[28px]
                      mb-1
                    "
                  >
                    {tWizard('sections.dietary')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {dietaryPreferences && dietaryPreferences.length > 0 ? (
                      dietaryPreferences.map((pref, index) => (
                        <div
                          key={index}
                          className="
                            text-[var(--Color-Grays-Gray-10,#0D0C07)]
                            text-[14px]
                            font-normal
                            leading-[24px]
                            bg-[var(--Color-Brand-Primary,#E2E1DF)]
                            px-3 py-1
                            rounded-[6px]
                            inline-block
                          "
                        >
                          {pref.label || pref}
                        </div>
                      ))
                    ) : (
                      <div
                        className="
                          text-[var(--Color-Grays-Gray-10,#0D0C07)]
                          text-[14px]
                          font-normal
                          leading-[24px]
                          bg-[var(--Color-Brand-Primary,#E2E1DF)]
                          px-3 py-1
                          rounded-[6px]
                          inline-block
                        "
                      >
                        {tCommon('premium.none')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Fixed Sticky Area */}
        <div
          className="
            fixed bottom-[16px] left-[24px] right-[24px]
            py-2 px-2
            flex justify-center
            bg-[linear-gradient(145deg,_rgba(255,255,255,0.9)_0%,_rgba(255,255,255,0.6)_100%)]
            backdrop-blur-lg
            z-50
            mx-auto
            rounded-[96px]
            border-[0.5px] border-[linear-gradient(145deg,_rgba(255,255,255,0.7)_0%,_rgba(255,255,255,1)_100%)]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
          "
        >
          <div className="w-full max-w-5xl gap-4 flex items-center justify-between">
            <button
              className={`
                inline-flex justify-center items-center gap-1
                h-[48px] px-[24px] py-[14px]
                rounded-[32px]
                border-[2px] border-[linear-gradient(200deg,_#000%,_#000_100%)]
                text-[#0D0C07] text-center
                text-[14px] font-medium leading-[28px]
                transition-colors
                w-full
                bg-[#E2E1DF]
                shadow-[0_2px_16px_0_rgba(50,50,50,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(226,225,223,1)]
                cursor-pointer bg-[linear-gradient(200deg,_#0D0C07%,_#353227_100%)] md:hover:border-[2px] md:hover:border-[#D7D7D5] md:hover:bg-[#D7D7D5]
              `}
              onClick={onTryAgain}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 3.00001V7.00001M22 5.00001H18M4 17V19M5 18H3M9.937 15.5C9.84772 15.1539 9.66734 14.8381 9.41462 14.5854C9.1619 14.3327 8.84607 14.1523 8.5 14.063L2.365 12.481C2.26033 12.4513 2.16821 12.3883 2.10261 12.3014C2.03702 12.2146 2.00153 12.1088 2.00153 12C2.00153 11.8912 2.03702 11.7854 2.10261 11.6986C2.16821 11.6118 2.26033 11.5487 2.365 11.519L8.5 9.93601C8.84595 9.84681 9.16169 9.66658 9.4144 9.41404C9.66711 9.16151 9.84757 8.84589 9.937 8.50001L11.519 2.36501C11.5484 2.25992 11.6114 2.16735 11.6983 2.1014C11.7853 2.03545 11.8914 1.99976 12.0005 1.99976C12.1096 1.99976 12.2157 2.03545 12.3027 2.1014C12.3896 2.16735 12.4526 2.25992 12.482 2.36501L14.063 8.50001C14.1523 8.84608 14.3327 9.1619 14.5854 9.41462C14.8381 9.66734 15.1539 9.84773 15.5 9.93701L21.635 11.518C21.7405 11.5471 21.8335 11.61 21.8998 11.6971C21.9661 11.7841 22.0021 11.8906 22.0021 12C22.0021 12.1094 21.9661 12.2159 21.8998 12.3029C21.8335 12.39 21.7405 12.4529 21.635 12.482L15.5 14.063C15.1539 14.1523 14.8381 14.3327 14.5854 14.5854C14.3327 14.8381 14.1523 15.1539 14.063 15.5L12.481 21.635C12.4516 21.7401 12.3886 21.8327 12.3017 21.8986C12.2147 21.9646 12.1086 22.0003 11.9995 22.0003C11.8904 22.0003 11.7843 21.9646 11.6973 21.8986C11.6104 21.8327 11.5474 21.7401 11.518 21.635L9.937 15.5Z" stroke="black" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{t('actions.regenerate')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MealPlanDetailsDrawer;


